import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SaveIcon from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { ButtonContainer } from "work/_views/work-page-styled-components";
import { SaveButton, SaveButtonLabel } from "work/proposals/_views/_shared/proposal-styled-components";

/** The dropdown's name, since it has no text of its own. Exported for the specs and the browser harness. */
export const MORE_WAYS_TO_SAVE_LABEL = "More ways to save";

/** Another way to save, behind the arrow beside Save — today, Save as Proposal Template (#6260). */
export type ProposalSaveOption = {
  key: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

const CompactGroup = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
}));

const CompactArrow = styled(IconButton)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: 0,
}));

/**
 * The proposal page's Save, with the other ways to save behind an arrow beside it (#6260).
 *
 * The arrow does not share Save's disabled state. Save waits for an edit; saving the proposal as a
 * template saves what is already saved, so it is offered while Save has nothing to do. With no
 * options the arrow is left out and this is the plain Save it always was.
 *
 * `compact` is the narrow pane's header, where Save is an icon with a caption under it.
 */
export default function ProposalSaveSplitButton(
  props: Readonly<{
    compact: boolean;
    disabled: boolean;
    loading: boolean;
    /** Why Save is disabled, or what it will do. Shown on Save alone, not on the arrow. */
    tooltip: ReactNode;
    options: readonly ProposalSaveOption[];
    onSave: () => void;
  }>,
) {
  const { compact, disabled, loading, tooltip, options, onSave } = props;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const arrowProps = {
    "aria-label": MORE_WAYS_TO_SAVE_LABEL,
    "aria-haspopup": "menu" as const,
    "aria-expanded": Boolean(menuAnchor),
    onClick: (event: ReactMouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget),
  };
  const menu = (
    <Menu
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={() => setMenuAnchor(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.key}
          disabled={option.disabled}
          onClick={() => {
            setMenuAnchor(null);
            option.onClick();
          }}
        >
          <ListItemIcon>{option.icon}</ListItemIcon>
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </Menu>
  );

  if (compact) {
    return (
      <CompactGroup>
        <Tooltip title={tooltip}>
          <span>
            <ButtonContainer>
              <SaveButton
                // The visible "Save" caption is the button's sibling, not its content, and the tooltip
                // above names the wrapper. Without this the control reaches a screen reader unnamed.
                aria-label="Save"
                size="medium"
                color="primary"
                disabled={disabled}
                onClick={onSave}
              >
                <SaveIcon fontSize="medium" />
              </SaveButton>
              <SaveButtonLabel variant="button" color="primary" disabled={disabled}>
                Save
              </SaveButtonLabel>
            </ButtonContainer>
          </span>
        </Tooltip>
        {options.length > 0 && (
          <>
            <CompactArrow color="primary" {...arrowProps}>
              <ArrowDropDownIcon />
            </CompactArrow>
            {menu}
          </>
        )}
      </CompactGroup>
    );
  }

  const save = (
    <Tooltip title={tooltip}>
      {/* A span keeps the tooltip reachable while Save is disabled. */}
      <span>
        <LoadingButton
          variant="contained"
          size="large"
          loading={loading}
          color="primary"
          disabled={disabled}
          startIcon={<SaveIcon />}
          onClick={onSave}
        >
          Save
        </LoadingButton>
      </span>
    </Tooltip>
  );
  if (options.length === 0) return save;

  return (
    <>
      <ButtonGroup variant="contained" size="large" disableElevation>
        {save}
        <Button size="small" {...arrowProps}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      {menu}
    </>
  );
}
